/**
 * Making chords from scales http://www.ethanhein.com/wp/2015/making-chords-from-scales/
 */
import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'
import invariant from 'tiny-invariant'
import { getAllChords as getAllChordsLib } from '../chord/all-chords.js'
import type { Chord, ChordVariant } from '../chord/chord-types.js'
import { isChordNotes } from '../chord/chord-types.js'
import { chordEquals } from '../chord/equals.js'
import { getNotes as getChordNotes } from '../chord/get-notes.js'
import type { NoteDegree } from '../note/index.js'
import * as NoteLib from '../note/index.js'
import symbols from '../symbols.js'
import { ALL_MODES } from './mode.js'
import type { ScaleDegree } from './scale-degree.js'
import { SCALE_DEGREE_NAMES, getScaleDegreeByIndex, getScaleDegreeNames, getScaleIndex } from './scale-degree.js'
import type { ScaleSpec } from './types.js'

export type Degree = 'I' | 'II' | 'III' | 'IV' | 'V' | 'VI' | 'VII' | 'VIII'

const MODE_INTERVALS: Record<string, NoteDegree[]> = {}
for (const [name, m] of Object.entries(ALL_MODES)) {
  MODE_INTERVALS[name] = m.intervals
}

const SCALES: Record<ScaleType, ScaleSpec> = {
  major: {
    intervals: ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'],
    chordTypes: {
      I: '',
      II: 'm',
      III: 'm',
      IV: '',
      V: '',
      VI: 'm',
      VII: 'dim',
      VIII: '',
    },
  },
  minor: {
    // 1, 2, ♭3, 4, 5, ♭6, ♭7, 8
    intervals: ['I', 'II', 'bIII', 'IV', 'V', 'bVI', 'bVII', 'VIII'],
    chordTypes: { I: 'm', II: 'dim', III: '', IV: 'm', V: 'm', VI: '', VII: '', VIII: 'm' },
  },

  /** Add the modes */
  ...ALL_MODES,

  majorpentatonic: {
    intervals: ['I', 'II', 'III', 'V', 'VI', 'VIII'],
  },
  minorpentatonic: {
    intervals: ['I', 'bIII', 'IV', 'V', 'bVII', 'VIII'],
  },

  harmonicminor: {
    // 1, 2, ♭3, 4, 5, ♭6, 7, 8
    // Diatonic triads in A harmonic minor (A-B-C-D-E-F-G#):
    // i (Am), ii° (B°), ♭III+ (C aug — flat-3 with augmented 5), iv (Dm),
    // V (E major — the whole point of harmonic minor: raised leading-tone),
    // ♭VI (F major), vii° (G#° — diminished on the leading tone).
    // Pre-2026-05-14 the table was a copy of natural-minor's, which mis-typed
    // III/V/VII (no harmonic-minor song's chord analysis was correct).
    intervals: ['I', 'II', 'bIII', 'IV', 'V', 'bVI', 'VII', 'VIII'],
    chordTypes: { I: 'm', II: 'dim', III: 'aug', IV: 'm', V: '', VI: '', VII: 'dim', VIII: 'm' },
  },

  melodicminor: {
    // 1, 2, ♭3, 4, 5, 6, 7, 8 — the ascending form (jazz minor scale; equal to
    // natural minor with raised 6 AND raised 7). Classical theory descends as
    // natural minor, but contemporary practice treats melodic minor as a single
    // scale in both directions.
    //
    // Diatonic triads in A melodic minor (A-B-C-D-E-F#-G#):
    //   i    (Am   = A-C-E)    — chord on 1, minor (same as natural/harmonic minor)
    //   ii   (Bm   = B-D-F#)   — chord on 2, MINOR (natural minor's ii° becomes ii
    //                            because raised 6 makes F# instead of F)
    //   ♭III+ (C aug = C-E-G#) — chord on flat-3, augmented (same as harmonic minor)
    //   IV   (D    = D-F#-A)   — chord on 4, MAJOR (natural minor's iv becomes IV
    //                            because raised 6 makes F# instead of F)
    //   V    (E    = E-G#-B)   — chord on 5, major (raised leading-tone, like
    //                            harmonic minor)
    //   vi°  (F#°  = F#-A-C)   — chord on natural-6, diminished
    //   vii° (G#°  = G#-B-D)   — chord on natural-7, diminished (leading-tone dim)
    //
    // All seven labels are already in `ChordFunction` from v1 + v1.5a + v1.5c —
    // no new union additions needed for melodic minor.
    intervals: ['I', 'II', 'bIII', 'IV', 'V', 'VI', 'VII', 'VIII'],
    chordTypes: { I: 'm', II: 'm', III: 'aug', IV: '', V: '', VI: 'dim', VII: 'dim', VIII: 'm' },
  },

  // augmented: {
  //   type: 'major',
  //   intervals: ['I', 'II', 'bIII', 'IV', 'V', 'bVI', 'VII', 'VIII'],
  // },

  // diminished: {
  //   type: 'minor',
  //   intervals: [0, 2, 1, 2, 1, 2, 1, 2, 1],
  // },

  // blues: {
  //   type: 'minor',
  //   intervals: [0, 3, 2, 1, 1, 3, 2],
  // },

  // /** Other scales */
  // spanish: [0, 1, 3, 1, 2, 1, 2, 2],
  // /** Japanese */
  // insen: [0, 1, 4, 2, 3, 2],
  // hirajoshi: [0, 1, 4, 2, 1, 4],
}

export const ALL_SCALES = Object.keys(SCALES) as ScaleType[]

export interface ScaleMatch {
  scale: Scale
  inScale: NoteName[]
  outOfScale: NoteName[]
  score: number
}

const equals = (a: Scale, b: Scale) => a && b && a?.root === b?.root && a?.type === b?.type

const spec = (scale: Scale): ScaleSpec => (scale.type ? SCALES[scale.type] : SCALES['major'])

const fromChords = (chords: ChordVariant[]): Scale[] => {
  const notes = chords.map(c => getChordNotes(c)).reduce((all, n) => [...all, ...n], [])
  return fromNotes(notes)
}

const isMode = (scale: Scale) => scale.type !== 'major' && scale.type !== 'minor'
const isKey = (scale: Scale) => !isMode(scale)

export const getScaleSpec = (scale: Scale): ScaleSpec => {
  const type = scale.type ?? 'major'
  return SCALES[type]
}

/**
 * Return scales where one or more notes are part of the scale.
 *
 * @param notes the notes to compare with scale(s)
 * @param scale compare to this specific scale. Otherwise match with all scales.
 * @returns Scales that have one or more notes, in sorted order (highest score first).
 */
const scaleMatch = (notes: NoteName[], scale?: Scale): ScaleMatch[] => {
  const match: ScaleMatch[] = []
  /** We don't suggest scales where the tonic is not in the set of notes */
  const scales: Scale[] = notes.map(n => ALL_SCALES.map(s => ({ root: n, type: s }))).flat()
  const compareScales = scale ? [scale] : scales

  const noteIdx = notes.map(n => ({ note: n, idx: NoteLib.getNoteChromaticIndex(n) }))

  for (const candidateScale of compareScales) {
    const scaleNotes = getNotes(candidateScale)
    const candIdx = scaleNotes.map(n => NoteLib.getNoteChromaticIndex(n))
    const outOfScale = [...noteIdx].filter(x => !candIdx.includes(x.idx))
    const inScale = [...noteIdx].filter(x => !outOfScale.includes(x))

    /** Don't create duplicates */
    if (match.find(s => equals(s.scale, candidateScale))) {
      continue
    }
    const score = inScale.length / notes.length
    match.push({
      scale: candidateScale,
      inScale: inScale.map(n => n.note),
      outOfScale: outOfScale.map(n => n.note),
      score,
    })
  }

  return match.sort((a, b) => (a.score === b.score ? 0 : a.score > b.score ? -1 : 1))
}

const scaleMatchChords = (chords: Chord[], scale?: Scale): ScaleMatch[] => {
  /** First get all the notes from the chords and make them unique */
  const notes = [...new Set(chords.map(getChordNotes).flat())]

  /** Step 1 - match against the notes */
  const matches = scaleMatch(notes, scale)

  const boosted = matches.map(m => {
    let { score } = m
    /** Step 2 - re-rank (90% for every match, 80% for modes) */
    score = score * (isMode(m.scale) ? 0.9 : 1.0)
    /** Step 3 - boost scales that have the I chord, and more if it is the first chord. Applies only to ChordVariants */
    const hasIChord = Boolean(chords.find(chord => getChordDegree(m.scale, chord) === 'I'))
    const firstChordIsIChord = getChordDegree(m.scale, chords[0]) === 'I'
    score = hasIChord ? score * (firstChordIsIChord ? 1.0 : 0.95) : score * 0.9

    return { ...m, score }
  })

  return boosted.sort((a, b) => (a.score === b.score ? 0 : a.score > b.score ? -1 : 1))
}

/** Return scales where all notes are in the scale */
const fromNotes = (scaleNotes: NoteName[]): Scale[] =>
  scaleMatch(scaleNotes)
    .filter(m => m.score === 1.0)
    .map(m => m.scale)

const getName = (scale?: Scale | null) => (scale ? `${scale.root} ${scale.type}` : '-')

const scaleNotesCache: { [scale: string]: NoteName[] } = {}

const getNotesChromaticIndices = (scale: Scale) => {
  const notes = getNotes(scale)
  return notes.map(n => NoteLib.getNoteChromaticIndex(n))
}

const getNotes = (scale: Scale) => {
  const scaleName = getName(scale)
  if (scaleNotesCache[scaleName]) {
    return scaleNotesCache[scaleName]
  }

  /** @todo this supports only seven-notes scales */
  const scaleInfo = getScaleSpec(scale)
  const degrees = scaleInfo.intervals

  const notes: NoteName[] = [scale.root]
  let currentNoteName = scale.root

  for (const degree of degrees.slice(1)) {
    const nextNoteDistance = NoteLib.getNoteDegreeChromaticDistance(degree)
    const nextNote = NoteLib.getNoteByChromaticOffset(scale.root, nextNoteDistance, currentNoteName)
    notes.push(nextNote)
    currentNoteName = nextNote
  }
  scaleNotesCache[scaleName] = notes
  return notes
}

export const getNotesWithOctave = (scale: Scale, startOctave: NoteLib.Octave): NoteLib.NoteOctave[] => {
  // Same as getNotes, but with octaves
  const rootChromaticIndex = NoteLib.getNoteChromaticIndex(scale.root)
  const notes = getNotes(scale)
  const notesWithOctave = notes.map((note, index) => {
    // If the note is the same or lower chromatic index,
    // it is either the root note or a note that should be played in the next octave
    const noteChromaticIndex = NoteLib.getNoteChromaticIndex(note)
    const octave = index === 0 ? startOctave : noteChromaticIndex > rootChromaticIndex ? startOctave : startOctave + 1
    return `${note}${octave}` as NoteLib.NoteOctave
  })
  return notesWithOctave
}

const noteDegreeToScaleDegree = (degree: NoteDegree): ScaleDegree => {
  const isbb = degree.includes('bb')
  const scaleDegree = degree.replaceAll('b', '').replaceAll('#', '') as ScaleDegree
  // If it's a bb (i.e., two flats) then the degree is one lower
  if (isbb) {
    const index = getScaleIndex(scaleDegree)
    const newIndex = index === 0 ? 6 : index - 1
    return getScaleDegreeByIndex(newIndex)
  }
  return scaleDegree
}

const getNoteByDegree = (scale: Scale, degree: NoteDegree) => {
  const index = getScaleIndex(noteDegreeToScaleDegree(degree))
  return getNotes(scale)[index]
}

/** Get the basic chords in the scale - i.e., one for each degree */
const getChords = (scale: Scale) => {
  // Check if the scale has a standard chord map
  const spec = getScaleSpec(scale)
  const chordTypes = spec.chordTypes
  if (!chordTypes) {
    return []
  }
  // @todo we require chordTypes - i.e., that 7 chords are defined
  return getScaleDegreeNames()
    .map(d => getChord(scale, d))
    .filter(Boolean)
}

const getChordByScaleDegree = (scale: Scale, degree: ScaleDegree) => {
  const chords = getChords(scale)
  const names = getScaleDegreeNames()
  const index = names.indexOf(degree)
  return chords[index] ?? null
}

/** Get all the chords from the library that have all in-scale notes */
const getAllChords = (scale: Scale | null) => getAllChordsLib().filter(c => (scale ? inScale(scale, c) : true))

const getIntervals = (scale: Scale) => getScaleSpec(scale).intervals
const getInterval = (scale: Scale, note: NoteName | null) => {
  if (!note) {
    return null
  }
  // Get the intervals from the scale
  const intervals = getIntervals(scale)
  // Handle the enharmonics
  const scaleNotesChromatic = getNotes(scale).map(NoteLib.getNoteChromaticIndex)
  const noteChromatic = NoteLib.getNoteChromaticIndex(note)

  const intervalsIndex = scaleNotesChromatic.indexOf(noteChromatic)

  return intervals[intervalsIndex]
}

const getChordByScaleDegrees = (scale: Scale, noteDegrees: ScaleDegree[]) => {
  /** Pick from the scale */
  const scaleNotes = getNotes(scale)
  return noteDegrees.map(degree => scaleNotes[getScaleIndex(degree)])
}

const getChord = (scale: Scale, degree: NoteDegree) => {
  const { chordTypes } = spec(scale)

  if (!chordTypes) {
    return undefined
  }
  const scaleDegree = noteDegreeToScaleDegree(degree)
  const variant = chordTypes[scaleDegree]
  if (variant === undefined) {
    return undefined
  }
  return {
    root: getNoteByDegree(scale, degree),
    variant,
  }
}

/**
 * Find the scale degree from the chord. The chord must match perfectly and without e.g., 7ths
 *
 * If you need to find whether a chord is inside of a scale, use @ref inScale instead
 */
const getChordDegree = (scale: Scale, ch?: Chord) => {
  /** Use the tonic of the chord */
  if (!ch) {
    return undefined
  }

  /** @todo support ChordNotes as well? */
  if (isChordNotes(ch)) {
    return
  }

  const chordRootDegree = getInterval(scale, ch.root)
  if (!chordRootDegree) {
    return
  }
  const scaleDegree = noteDegreeToScaleDegree(chordRootDegree)
  const c = getChord(scale, scaleDegree)
  return chordEquals(c, ch) ? scaleDegree : undefined
}

/**
 * Augment chord degree based on the scale:
 * - major triads: uppercase Roman numeral (`III`)
 * - minor triads: lowercase Roman numeral (`iii`)
 * - diminished triads: lowercase + `°` (`iii°`)
 * - augmented triads: uppercase + `+` (`III+`) — harmonic minor's III is the
 *   primary surface here; the conventional notation for an augmented triad on
 *   a scale degree is uppercase-with-plus.
 */
const prettyScaleChordDegree = (scale: Scale, chordDegree: ScaleDegree) => {
  const scaleSpec = getScaleSpec(scale)
  const chordType = scaleSpec.chordTypes?.[chordDegree]
  if (!chordType) {
    return chordDegree
  }
  const chordTypeName =
    chordType === 'dim'
      ? `${chordDegree.toLowerCase()}${symbols.DIMINISHED}`
      : chordType === 'm'
        ? chordDegree.toLowerCase()
        : chordType === 'aug'
          ? `${chordDegree}+`
          : chordDegree
  return chordTypeName
}

/**
 *
 * @param scale The scale
 * @param chord The chord to evaluate.
 * @returns true if the chord is in the scale, i.e., contains only notes in the scale, false otherwise.
 */
const inScale = (scale: Scale, chord: Chord) => {
  const notes = isChordNotes(chord) ? chord.notes : getChordNotes(chord)
  const chordNotesChromatic = notes.map(n => NoteLib.getNoteChromaticIndex(n))
  const scaleChromatic = getNotes(scale).map(n => NoteLib.getNoteChromaticIndex(n))

  const difference = [...chordNotesChromatic].filter(x => !scaleChromatic.includes(x))
  return difference.length === 0
}

const noteInScale = (scale: Scale, note?: NoteName | null) => {
  const scaleChromatic = getNotes(scale).map(n => NoteLib.getNoteChromaticIndex(n))
  return note ? scaleChromatic.includes(NoteLib.getNoteChromaticIndex(note)) : false
}

/**
 * Get the correct name for a note in the scale, e.g., A# in D major will map to Bb.
 * If the note is not in the scale, it is left unchanged.
 */
const getScaleNoteName = (scale: Scale, note: NoteName) => {
  const chromatic = NoteLib.getNoteChromaticIndex(note)
  const scaleNotes = getNotes(scale)
  const scaleChromatic = scaleNotes.map(n => NoteLib.getNoteChromaticIndex(n))
  const index = scaleChromatic.indexOf(chromatic)
  return index === -1 ? note : scaleNotes[index]
}

const transpose = (scale: Scale, semiTones: number) => {
  const transposed = NoteLib.transpose(scale.root, semiTones)
  return { ...scale, root: transposed }
}

export const sortByDegree = (scale: Scale, notes: NoteName[] = []) =>
  notes.sort((a, b) => {
    const degreeA = getInterval(scale, a)
    const degreeB = getInterval(scale, b)

    return (
      (degreeA ? NoteLib.getNoteDegreeInfo(degreeA).chromaticDistance : Number.MAX_SAFE_INTEGER) -
      (degreeB ? NoteLib.getNoteDegreeInfo(degreeB).chromaticDistance : Number.MAX_SAFE_INTEGER)
    )
  })

/**
 * Get the relative major / minor scale. It's the 6th degree from major to minor, and 3nd from minor to major.
 * http://www.simplifyingtheory.com/relative-minor-major/
 */

const getRelative = (scale: Scale): Scale => {
  const degree = scale.type === 'major' ? 'VI' : 'III'
  return {
    root: getNoteByDegree(scale, degree),
    type: scale.type === 'major' ? 'minor' : 'major',
  }
}

type ScaleNoteFilter = (props: { scale: Scale; note: NoteName; idx: number; scaleDegree: ScaleDegree }) => boolean

export const scaleNoteFilters: Record<string, ScaleNoteFilter> = {
  /** TBD */
}

type ScaleFilterName = keyof typeof scaleNoteFilters

export const getScaleNotesWithFilter = (scale: Scale, filterName: ScaleFilterName) => {
  const filter = scaleNoteFilters[filterName]
  invariant(filter, `Filter ${filter} not found`)
  const scaleNotes = getNotes(scale)
  return scaleNotes
    .map((note, idx) => {
      const scaleDegree = SCALE_DEGREE_NAMES[idx]
      return filter({ note, scale, idx, scaleDegree }) ? note : null
    })
    .filter(Boolean) as NoteName[]
}

export default {
  SCALE_DEGREE_NAMES,
  fromChords,
  fromNotes,
  getChord,
  getChords,
  getAllChords,
  getChordDegree,
  getChordByScaleDegrees,
  getNotes,
  getNoteByDegree,
  getScaleNoteName,
  getName,
  inScale,
  noteInScale,
  getRelative,
  isMode,
  isKey,
  equals,
  scaleMatch,
  scaleMatchChords,
  sortByDegree,
  transpose,
  getNotesWithOctave,
  getScaleSpec,
  getIntervals,
  getInterval,
  prettyScaleChordDegree,
  getChordByScaleDegree,
  getNotesChromaticIndices,
  noteDegreeToScaleDegree,
}
